import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { Page } from '../types/Page';

// A single recently-visited leaf page, persisted to localStorage. Stored as a
// flat snapshot (not a Page) so the bottom sidebar section can render without
// re-resolving against the live pages tree.
export interface RecentEntry {
  url: string;
  displayName: string;
  iconName: string | null;
}

// Module-scope key + safe load/save helpers, mirroring Sidebar's
// EXPANDED_STORAGE_KEY pattern (try/catch, ignore storage failures).
const RECENT_STORAGE_KEY = 'lop:sidebar:recent';

// Open/closed state key for the recent section, owned by SidebarRecent.tsx but
// shared here so clearRecentPages can wipe both keys on logout.
export const RECENT_OPEN_STORAGE_KEY = 'lop:sidebar:recent:open';

// How many entries we keep in storage vs. how many we surface in the UI.
const STORED_CAP = 8;
const DISPLAY_CAP = 5;

function isRecentEntry(x: unknown): x is RecentEntry {
  if (typeof x !== 'object' || x === null) return false;
  const e = x as Record<string, unknown>;
  return (
    typeof e.url === 'string' &&
    typeof e.displayName === 'string' &&
    (typeof e.iconName === 'string' || e.iconName === null)
  );
}

function load(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecentEntry) : [];
  } catch {
    return [];
  }
}

function save(entries: RecentEntry[]): void {
  try {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // storage unavailable; ignore
  }
}

// Wipe the persisted recent list + its open/closed state. Called on logout so
// the next user can't see the previous user's recently visited screens
// (banking privacy).
export function clearRecentPages(): void {
  try {
    localStorage.removeItem(RECENT_STORAGE_KEY);
    localStorage.removeItem(RECENT_OPEN_STORAGE_KEY);
  } catch {
    // storage unavailable; ignore
  }
}

export function useRecentPages(pages: Page[] | null): RecentEntry[] {
  const location = useLocation();
  const [entries, setEntries] = useState<RecentEntry[]>(() => load());
  // The pathname the session started on (component mount). Used to skip
  // recording the landing page — see the effect below.
  const landingPathRef = useRef(location.pathname);
  // Flips true once the user navigates away from the landing page; only then do
  // we start recording.
  const armedRef = useRef(false);

  useEffect(() => {
    if (!pages) return;
    const pathname = location.pathname;

    // Don't record the page the session landed on. After an explicit logout,
    // re-login restores the pre-logout URL (LoginPage redirects to `from`), so
    // recording the landing page would let the previous screen resurface as
    // "recently visited" once the user navigates onward. Once the user has
    // navigated away from the landing page, record normally — including if they
    // later return to it.
    if (!armedRef.current) {
      if (pathname === landingPathRef.current) return;
      armedRef.current = true;
    }

    // Only record real leaf screens (a page whose url matches the current
    // route), and never the home page — it's reached via the logo, not the
    // recent list.
    const match = pages.find(
      (p) => p.url === pathname && p.pageCode !== 'home',
    );
    if (!match) return;

    const entry: RecentEntry = {
      url: match.url as string,
      displayName: match.displayName,
      iconName: match.iconName,
    };

    setEntries((prev) => {
      // Move-to-front with dedupe by url, capped at STORED_CAP.
      const next = [entry, ...prev.filter((e) => e.url !== entry.url)].slice(
        0,
        STORED_CAP,
      );
      save(next);
      return next;
    });
  }, [location.pathname, pages]);

  // The displayed list excludes the current page (so it offers screens to go
  // BACK to) and is capped at DISPLAY_CAP.
  return entries
    .filter((e) => e.url !== location.pathname)
    .slice(0, DISPLAY_CAP);
}
